import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  EmptyState,
  GhostButton,
  PageHeader,
  Panel,
  StatusBadge,
  relativeTime,
  shortDate,
} from "@/components/admin/uiKit";

const TEMPLATE_FILTERS = [
  { id: "all", label: "All" },
  { id: "welcome", label: "Welcome" },
  { id: "service_reminder", label: "Reminder" },
  { id: "service_completed", label: "Service Done" },
  { id: "payment_issue", label: "Payment Issue" },
  { id: "custom_quote_admin", label: "Quote (admin)" },
  { id: "custom_quote_customer", label: "Quote (customer)" },
  { id: "cancellation_confirmed", label: "Cancellation" },
];

export default function AdminEmailLog() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    const params = filter === "all" ? {} : { template: filter };
    adminApi
      .get("/admin/email-log", { params })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatAdminError(err)));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <div data-testid="admin-email-log">
      <PageHeader
        overline="System"
        title="Email Log"
        description="Read-only view of every email the system has queued. In mock mode these are not actually delivered."
        actions={
          <GhostButton onClick={load} data-testid="admin-email-log-refresh">
            <RefreshCw size={14} /> Refresh
          </GhostButton>
        }
      />

      <div className="flex flex-wrap gap-2 mb-5">
        {TEMPLATE_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setFilter(s.id)}
            className={`text-xs font-bold uppercase tracking-[0.18em] px-3 py-2 rounded-lg border transition-colors ${
              filter === s.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`admin-email-log-filter-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : items.length === 0 ? (
        <EmptyState
          title="No emails in this bucket"
          description="The email log fills up as the app sends welcome, completion, and quote emails."
        />
      ) : (
        <Panel className="overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {items.map((e) => {
              const open = expanded === e.id;
              return (
                <li
                  key={e.id}
                  className="px-5 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`admin-email-row-${e.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : e.id)}
                    className="w-full flex items-center justify-between gap-3 text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {open ? (
                        <ChevronDown size={14} className="text-slate-400 shrink-0" />
                      ) : (
                        <ChevronRight size={14} className="text-slate-400 shrink-0" />
                      )}
                      <div className="h-8 w-8 rounded-md bg-sky-50 text-sky-700 grid place-items-center shrink-0">
                        <Mail size={14} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-700">
                            {e.template?.replace(/_/g, " ")}
                          </p>
                          <StatusBadge status={e.status} />
                        </div>
                        <p className="text-sm text-slate-900 truncate">{e.subject}</p>
                        <p className="text-xs text-slate-500 truncate">
                          To {e.to_email} · {shortDate(e.created_at)} · {relativeTime(e.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="mt-3 ml-9 grid gap-3 lg:grid-cols-2">
                      <div className="rounded-lg border border-slate-200 p-3">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
                          Payload (context)
                        </p>
                        <pre
                          className="text-[11px] text-slate-700 max-h-72 overflow-auto whitespace-pre-wrap"
                          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                        >
                          {JSON.stringify(e.payload || {}, null, 2)}
                        </pre>
                      </div>
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold p-3 border-b border-slate-200 bg-slate-50">
                          Rendered HTML
                        </p>
                        <iframe
                          title={`email-${e.id}`}
                          srcDoc={e.body_html || "<p>(no body)</p>"}
                          className="w-full h-72 bg-white"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Panel>
      )}
    </div>
  );
}
